import { CheckOutlined, CodeOutlined, CopyOutlined, EyeOutlined } from '@ant-design/icons';
import { Segmented, Tooltip, message } from 'antd';
import React, { useState } from 'react';

/**
 * CodeBlockToolbar — the one and only toolbar across every RichCodeBlock,
 * everywhere: chat, posts, file viewer. Layout is always:
 *
 *   [language] ... [Rich | Raw] [Copy]
 *
 * The Rich/Raw toggle is always visible for visual consistency. When the
 * language has no rich representation (anything other than markdown or html),
 * the Rich option is rendered as disabled with an explanatory tooltip — so the
 * user can always see the toggle exists, without any "magic" hiding it.
 *
 * The copy button always copies the raw source regardless of active mode.
 */
const CodeBlockToolbar = ({
  language,
  richSupported,
  hideToggle = false,
  mode,
  onModeChange,
  rawCode,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      message.error('Failed to copy code');
    }
  };

  const handleModeChange = (newMode) => {
    // Guard: user clicking a disabled option shouldn't do anything. Ant
    // Segmented already blocks clicks on option-level `disabled`, but we keep
    // this as a belt-and-braces safety net in case future Ant versions change.
    if (newMode === 'rich' && !richSupported) return;
    onModeChange(newMode);
  };

  // Rich is always rendered; the option itself carries the disabled state so
  // the control is always the same shape across every surface.
  const richOption = {
    value: 'rich',
    icon: <EyeOutlined />,
    label: 'Rich',
    disabled: !richSupported,
  };

  const richSegment = (
    <Segmented
      size="small"
      value={mode}
      onChange={handleModeChange}
      options={[
        richOption,
        { label: 'Raw', value: 'raw', icon: <CodeOutlined /> },
      ]}
    />
  );

  return (
    <div className="rich-code-block-toolbar">
      <span className="rich-code-block-language">{language || 'text'}</span>

      <div className="rich-code-block-toolbar-actions">
        {!hideToggle && (
          richSupported ? (
            richSegment
          ) : (
            <Tooltip title="Rich preview is only available for Markdown, HTML and CSV">
              <span className="rich-code-block-segmented-wrapper">
                {richSegment}
              </span>
            </Tooltip>
          )
        )}
        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
          <button
            type="button"
            className={`rich-code-block-copy-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
            aria-label="Copy code"
          >
            {copied ? <CheckOutlined /> : <CopyOutlined />}
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default CodeBlockToolbar;
